/* eslint-disable react/prop-types -- project uses JSX without PropTypes package */
import { useState, useEffect, useMemo, useRef } from "react";
import Card from "../Card";
import ErrorMessage from "../ErrorMessage";
import Loader from "../Loader";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReactTable from "../ReactTable";
import {
  createVendorSizingChart,
  getVendorBrands,
  getVendorSizingChartById,
  patchVendorSizingChart,
} from "../../services/products";
import {
  capitalize,
  formatChartData,
  formatClothingMeasurement,
  getErrorMessage,
  sanitizePlainText,
} from "../../helpers/functions";
import { TABLE_MEASUREMENTS } from "../../types/types";
import { createColumnHelper } from "@tanstack/react-table";
import InputControl, { InputLabel } from "../InputControl";
import PlusIcon from "../../assets/svg/icon_plus.svg?react";
import toast from "react-hot-toast";
import TrashIcon from "../../assets/svg/icon_trash.svg?react";
import { nanoid } from "nanoid";
import MeasurementUnitToggle from "./MeasurementUnitToggle";
import Select, { components } from "react-select";
import CreatableSelect from "react-select/creatable";
import ChevronDownIcon from "../../assets/svg/icon_chevron-down.svg?react";
import { useAvStore } from "../../store";
import { defaultSizingChart } from "../../helpers/defaultSizingChart";
import {
  SIZING_GENDER,
  SIZING_CLOTHING,
  getPrimaryMeasurementKeys,
  augmentSizesForSwim,
  inferGenderAndClothingFromChartId,
} from "../../helpers/sizingChartTemplates";

const MEASUREMENT_KEYS = ["neck", "chest", "sleeve", "hip", "waist", "inseam"];

/** Synthetic row appended to the matrix for inline “+ Add” (not a real measurement key). */
const ADD_MEASUREMENT_ROW_TITLE = "__addMeasurementRow__";

/** Body measurement keys that are optional for this clothing type (fixed API keys only). */
function getOptionalMeasurementKeysForClothing(clothingType) {
  const primary = getPrimaryMeasurementKeys(clothingType);
  return MEASUREMENT_KEYS.filter((k) => !primary.includes(k));
}

/** Map typed text to an available optional measurement key, or null. */
function matchOptionalMeasurementKey(rawInput, clothingType, sampleRow) {
  const optional = getOptionalMeasurementKeysForClothing(clothingType).filter(
    (k) => sampleRow?.[k] == null
  );
  const t = rawInput.trim().toLowerCase();
  if (!t) return null;
  if (optional.includes(t)) return t;
  const direct = optional.find((k) => k.toLowerCase() === t);
  if (direct) return direct;
  return optional.find((k) => capitalize(k).toLowerCase() === t) || null;
}

const TEMPLATE_BY_PICK = {
  [`${SIZING_GENDER.MALE}:${SIZING_CLOTHING.TOP}`]: "maleTopSizingChart",
  [`${SIZING_GENDER.MALE}:${SIZING_CLOTHING.BOTTOM}`]: "maleBottomSizingChart",
  [`${SIZING_GENDER.FEMALE}:${SIZING_CLOTHING.TOP}`]: "femaleTopSizingChart",
  [`${SIZING_GENDER.FEMALE}:${SIZING_CLOTHING.BOTTOM}`]: "femaleBottomSizingChart",
  [`${SIZING_GENDER.MALE}:${SIZING_CLOTHING.FULL_BODY}`]: "maleTopSizingChart",
  [`${SIZING_GENDER.FEMALE}:${SIZING_CLOTHING.FULL_BODY}`]: "femaleTopSizingChart",
  [`${SIZING_GENDER.MALE}:${SIZING_CLOTHING.SWIM}`]: "maleTopSizingChart",
  [`${SIZING_GENDER.FEMALE}:${SIZING_CLOTHING.SWIM}`]: "femaleTopSizingChart",
  [`${SIZING_GENDER.FEMALE}:${SIZING_CLOTHING.LINGERIE}`]: "femaleTopSizingChart",
};

const VENDOR_SIZING_CHART_FIELD_MAP = {
  gender: "gender",
  clothingType: "clothingType",
};

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

function normalizeMeasurementInput(v) {
  const raw = String(v ?? "");
  let out = "";
  let dotSeen = false;
  let digitCount = 0;
  let decimalCount = 0;

  for (const ch of raw) {
    if (ch >= "0" && ch <= "9") {
      if (digitCount >= 5) continue;
      if (dotSeen) {
        if (decimalCount >= 2) continue;
        decimalCount += 1;
      }
      out += ch;
      digitCount += 1;
      continue;
    }
    if (ch === "." && !dotSeen) {
      dotSeen = true;
      out += out.length ? "." : "0.";
    }
  }
  return out;
}

function rangeOrNull(val) {
  const raw = String(val ?? "").trim();
  if (!raw) return null;
  const n = parseFloat(raw);
  if (!Number.isFinite(n)) return null;
  return Math.min(999.99, Math.max(0, Number(n.toFixed(2))));
}

function isValidMeasurementValue(val) {
  if (val == null || String(val).trim() === "") return false;
  const s = String(val).trim();
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return false;
  const digitsOnly = s.replace(".", "");
  if (digitsOnly.length > 5) return false;
  const n = parseFloat(s);
  return Number.isFinite(n) && n >= 0 && n <= 999.99;
}

function validateMinMaxOrder(cell) {
  if (!cell || isEmptyMeasurementCell(cell)) return { ok: true };
  const minStr = String(cell.min ?? "").trim();
  const maxStr = String(cell.max ?? "").trim();
  if (!minStr || !maxStr) return { ok: true };
  if (!isValidMeasurementValue(cell.min) || !isValidMeasurementValue(cell.max)) return { ok: true };
  const min = rangeOrNull(cell.min);
  const max = rangeOrNull(cell.max);
  if (min == null || max == null) return { ok: true };
  if (min >= max) {
    return { ok: false, message: "Each minimum must be less than its maximum." };
  }
  return { ok: true };
}

function isEmptyMeasurementCell(cell) {
  if (!cell) return true;
  const min = String(cell.min ?? "").trim();
  const max = String(cell.max ?? "").trim();
  return !min && !max;
}

function toArrayPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function inferClothingType(sizes = []) {
  const sample = sizes[0] || {};
  const hasChest = sample.chest != null;
  const hasHip = sample.hip != null;
  const hasWaist = sample.waist != null;
  if (hasChest && hasHip) return SIZING_CLOTHING.FULL_BODY;
  if (hasWaist && !hasChest) return SIZING_CLOTHING.BOTTOM;
  return SIZING_CLOTHING.TOP;
}

function normalizeGender(value) {
  const lowered = String(value || "").trim().toLowerCase();
  if (lowered === SIZING_GENDER.MALE || lowered === SIZING_GENDER.FEMALE) return lowered;
  return "";
}

function normalizeClothingType(value) {
  const lowered = String(value || "").trim().toLowerCase();
  if (lowered === "swim" || lowered === "fullbody" || lowered === "full body") {
    return SIZING_CLOTHING.FULL_BODY;
  }
  if (Object.values(SIZING_CLOTHING).includes(lowered)) return lowered;
  return "";
}

function getNormalizedGenderAndClothingFromPayload({ payload, firstItem, incomingSizes }) {
  const payloadGender = normalizeGender(payload?.gender ?? payload?.Gender);
  const payloadClothingType = normalizeClothingType(
    payload?.clothingType ?? payload?.ClothingType
  );
  if (payloadGender && payloadClothingType) {
    return { gender: payloadGender, clothingType: payloadClothingType };
  }

  const inferredFromChartId = inferGenderAndClothingFromChartId(
    firstItem?.id ?? firstItem?.Id
  );
  if (inferredFromChartId?.gender && inferredFromChartId?.clothingType) {
    return inferredFromChartId;
  }

  // Backend may not return gender/clothingType yet; preserve current matrix-based fallback.
  const inferredClothingType = inferClothingType(incomingSizes);
  return {
    gender:
      inferredClothingType === SIZING_CLOTHING.LINGERIE
        ? SIZING_GENDER.FEMALE
        : SIZING_GENDER.MALE,
    clothingType: inferredClothingType,
  };
}

function normalizeSizeForUi(size) {
  const normalized = { name: size?.name || "Size", id: nanoid() };
  MEASUREMENT_KEYS.forEach((k) => {
    normalized[k] = size?.[k] ?? null;
  });
  return normalized;
}

function buildTemplateChart(gender, clothingType, options = {}) {
  const { emptyValues = false } = options;
  const templateId = TEMPLATE_BY_PICK[`${gender}:${clothingType}`];
  const item = clone(defaultSizingChart.items.find((x) => x.id === templateId));
  if (!item) return null;
  let sizes = item.sizes.map(normalizeSizeForUi);

  if (
    clothingType === SIZING_CLOTHING.SWIM ||
    clothingType === SIZING_CLOTHING.FULL_BODY
  ) {
    const bottomId =
      gender === SIZING_GENDER.MALE ? "maleBottomSizingChart" : "femaleBottomSizingChart";
    const bottom = clone(defaultSizingChart.items.find((x) => x.id === bottomId));
    if (bottom) {
      const swim = augmentSizesForSwim({
        topChart: { ...item, sizes },
        bottomChart: bottom,
        clothingType,
      });
      sizes = swim.sizes.map(normalizeSizeForUi);
    }
  }

  const primary = getPrimaryMeasurementKeys(clothingType);
  sizes = sizes.map((row) => {
    const next = { ...row };
    if (emptyValues) {
      MEASUREMENT_KEYS.forEach((k) => {
        next[k] = null;
      });
    }
    primary.forEach((k) => {
      next[k] = { min: "", max: "" };
    });
    return next;
  });
  return { itemId: templateId || "", sizes };
}

export default function SizingChartEditorForm({ routeChartId, mode = "edit" }) {
  const user = useAvStore((state) => state.user);
  const isPreview = mode === "preview";
  const isCreate = mode === "create";
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [savingError, setSavingError] = useState("");
  const [isSaving, setSaving] = useState(false);

  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [gender, setGender] = useState(SIZING_GENDER.MALE);
  const [clothingType, setClothingType] = useState(SIZING_CLOTHING.TOP);
  const [itemId, setItemId] = useState("");
  const [sizes, setSizes] = useState([]);
  const sizesRef = useRef(sizes);
  sizesRef.current = sizes;
  const [currentMeasurement, setCurrentMeasurement] = useState(
    searchParams.get("defaultMeasurement") || TABLE_MEASUREMENTS.CM
  );

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const b = await getVendorBrands();
        setBrands(toArrayPayload(b?.data ?? b));
        if (isCreate) {
          const tpl = buildTemplateChart(gender, clothingType, {
            emptyValues: true,
          });
          if (tpl) {
            setItemId(tpl.itemId || "");
            setSizes(tpl.sizes);
          }
        } else if (routeChartId) {
          const r = await getVendorSizingChartById({ id: routeChartId });
          const payload = r?.data ?? r;
          setBrandId(payload?.brandId ?? payload?.BrandId ?? "");
          setDisplayName(payload?.chartName ?? payload?.ChartName ?? "");
          setCurrentMeasurement(payload?.unit ?? payload?.Unit ?? TABLE_MEASUREMENTS.CM);
          const rawItems = Array.isArray(payload?.items)
            ? payload.items
            : Array.isArray(payload?.Items)
              ? payload.Items
              : [];
          const firstItem = rawItems[0];
          setItemId(firstItem?.id ?? firstItem?.Id ?? "");
          const rawSizes = Array.isArray(firstItem?.sizes)
            ? firstItem.sizes
            : Array.isArray(firstItem?.Sizes)
              ? firstItem.Sizes
              : [];
          const incomingSizes = rawSizes.map(normalizeSizeForUi);
          setSizes(incomingSizes);
          const resolved = getNormalizedGenderAndClothingFromPayload({
            payload,
            firstItem,
            incomingSizes,
          });
          setClothingType(resolved.clothingType);
          setGender(resolved.gender);
        }
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreate, routeChartId]);

  const applyGenderAndClothing = (nextGender, nextClothing) => {
    let g = nextGender;
    const c = nextClothing;
    if (c === SIZING_CLOTHING.LINGERIE) g = SIZING_GENDER.FEMALE;
    setGender(g);
    setClothingType(c);
    const tpl = buildTemplateChart(g, c, { emptyValues: isCreate });
    if (!tpl) {
      toast.error("Template unavailable for this combination.");
      return;
    }
    setItemId(tpl.itemId || "");
    setSizes(tpl.sizes);
  };

  const updateSizeName = (uiId, value) => {
    setSizes((prev) =>
      prev.map((row) =>
        row.id === uiId ? { ...row, name: sanitizePlainText(value, 12) } : row
      )
    );
  };

  const updateCell = (uiId, measurement, range, rawValue) => {
    const value = normalizeMeasurementInput(rawValue);
    setSizes((prev) =>
      prev.map((row) => {
        if (row.id !== uiId) return row;
        const nextCell = row[measurement] ? { ...row[measurement] } : { min: "", max: "" };
        nextCell[range] = value;
        return { ...row, [measurement]: nextCell };
      })
    );
  };

  const addSizeColumn = () => {
    if (!sizes.length) return;
    const sample = sizes[0];
    const next = { id: nanoid(), name: "New" };
    MEASUREMENT_KEYS.forEach((k) => {
      next[k] = sample[k] == null ? null : { min: "", max: "" };
    });
    setSizes((prev) => [...prev, next]);
  };

  const deleteSizeColumn = (uiId) => {
    if (sizes.length <= 1) {
      toast.error("You must have at least 1 column");
      return;
    }
    setSizes((prev) => prev.filter((s) => s.id !== uiId));
  };

  const addOptionalMeasurement = (key) => {
    setSizes((prev) =>
      prev.map((row) => ({
        ...row,
        [key]: row[key] != null ? row[key] : { min: "", max: "" },
      }))
    );
  };

  const removeMeasurementRow = (key) => {
    if (getPrimaryMeasurementKeys(clothingType).includes(key)) return;
    setSizes((prev) => prev.map((row) => ({ ...row, [key]: null })));
  };

  const chartDataForTable = useMemo(() => ({ sizes }), [sizes]);
  /** Stable across size label edits; only changes when columns are added/removed/reordered. */
  const sizeColumnSignature = useMemo(
    () => sizes.map((s) => s.id).join("|"),
    [sizes]
  );
  const optionalAddChoices = useMemo(() => {
    const sample = sizes[0] || {};
    return getOptionalMeasurementKeysForClothing(clothingType)
      .filter((k) => sample[k] == null)
      .map((k) => ({ value: k, label: capitalize(k) }));
  }, [sizes, clothingType]);

  const optionalChoiceSignature = useMemo(
    () => optionalAddChoices.map((o) => o.value).join(","),
    [optionalAddChoices]
  );

  const chartTableData = useMemo(() => {
    const rows = formatChartData(chartDataForTable, {
      includeEmptyMeasurements: true,
    });
    if (isPreview || optionalAddChoices.length === 0) return rows;
    return [
      ...rows,
      {
        title: ADD_MEASUREMENT_ROW_TITLE,
        measurements: [],
        isAddMeasurementRow: true,
      },
    ];
  }, [chartDataForTable, isPreview, optionalAddChoices.length]);

  function buildColumns() {
    const columnHelper = createColumnHelper();
    const primaryKeys = getPrimaryMeasurementKeys(clothingType);
    const matrixInputClass =
      "!rounded-md !min-h-[34px] !py-0.5 !px-1.5 !text-[15px] font-medium leading-tight !bg-transparent !w-full";

    const addMeasurementPlaceholderCells = (sizeId) => (
      <div
        className="flex flex-nowrap items-center gap-x-1.5 min-w-[124px] text-[15px] pointer-events-none select-none"
        aria-hidden
      >
        <div className="rounded-md bg-slate-100 shrink-0 min-w-[46px] max-w-[55px] w-[55px] px-1.5 py-1">
          <InputControl
            name={`add-ph-${sizeId}-min`}
            className="min-w-0"
            inputClassName={matrixInputClass}
            placeholder="min"
            value=""
            disabled
          />
        </div>
        <span className="text-slate-500 shrink-0">–</span>
        <div className="rounded-md bg-slate-100 shrink-0 min-w-[46px] max-w-[55px] w-[55px] px-1.5 py-1">
          <InputControl
            name={`add-ph-${sizeId}-max`}
            className="min-w-0"
            inputClassName={matrixInputClass}
            placeholder="max"
            value=""
            disabled
          />
        </div>
        <span className="text-xs text-slate-600 shrink-0">{currentMeasurement}</span>
      </div>
    );

    const base = [
      columnHelper.accessor("title", {
        header: () => <span>Measurement</span>,
        cell: (info) => {
          const row = info.row.original;
          if (row.isAddMeasurementRow) {
            return (
              <div className="flex items-center gap-2 w-full min-w-[130px] max-w-[140px] shrink-0">
                <CreatableSelect
                  classNamePrefix="react-select"
                  className="custom-select custom-select--sizing-add"
                  placeholder="+ Add"
                  options={optionalAddChoices}
                  value={null}
                  isClearable
                  isValidNewOption={(input) =>
                    Boolean(matchOptionalMeasurementKey(input, clothingType, sizes[0] || {}))
                  }
                  onChange={(opt) => opt?.value && addOptionalMeasurement(opt.value)}
                  onCreateOption={(input) => {
                    const key = matchOptionalMeasurementKey(input, clothingType, sizes[0] || {});
                    if (key) addOptionalMeasurement(key);
                    else {
                      toast.error(
                        "Only standard body measurements can be saved. Pick from the list or type a known name (e.g. Sleeve, Neck). The API accepts fixed measurement keys only."
                      );
                    }
                  }}
                  components={{ DropdownIndicator }}
                  menuPlacement="auto"
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : undefined
                  }
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 50 }),
                  }}
                />
              </div>
            );
          }
          const key = row.title;
          const canRemove = !isPreview && !primaryKeys.includes(key);
          return (
            <div className="flex items-center gap-2.5 min-w-[88px]">
              <p className="capitalize flex-1 font-medium text-slate-800">{key}</p>
              {canRemove ? (
                <button
                  type="button"
                  title="Remove measurement row"
                  className="p-1 rounded-md shrink-0 text-slate-400 hover:text-red-600 hover:bg-slate-100 transition-colors"
                  onClick={() => removeMeasurementRow(key)}
                >
                  <TrashIcon className="w-4 h-4" aria-hidden />
                </button>
              ) : null}
            </div>
          );
        },
      }),
    ];

    const dynamic = sizes.map((size) => {
      const sizeId = size.id;
      return columnHelper.accessor((row) => row, {
        id: `size-${sizeId}`,
        cell: (info) => {
          if (info.row.original.isAddMeasurementRow) {
            return addMeasurementPlaceholderCells(sizeId);
          }
          const measureKey = info.row.original.title;
          if (isPreview) {
            const stats = info.row.original.measurements.find((m) => m.id === sizeId);
            const a = formatClothingMeasurement({
              val: stats?.min,
              current: currentMeasurement,
              addUnits: true,
              originalMeasurement: currentMeasurement,
            });
            const b = formatClothingMeasurement({
              val: stats?.max,
              current: currentMeasurement,
              addUnits: true,
              originalMeasurement: currentMeasurement,
            });
            return <p className="text-sm min-w-[120px]">{a}-{b || "—"}</p>;
          }
          const rowSize = sizesRef.current.find((s) => s.id === sizeId);
          const cellData = rowSize?.[measureKey];
          return (
            <div className="flex flex-nowrap items-center gap-x-1.5 min-w-[124px] text-[15px]">
              <div className="rounded-md bg-slate-100 shrink-0 min-w-[46px] max-w-[55px] w-[55px] px-1.5 py-1">
                <InputControl
                  name={`${measureKey}-${sizeId}-min`}
                  className="min-w-0"
                  inputClassName={matrixInputClass}
                  placeholder="min"
                  value={cellData?.min ?? ""}
                  maxLength={6}
                  onChange={(e) =>
                    updateCell(sizeId, measureKey, "min", e.target.value)
                  }
                />
              </div>
              <span className="text-slate-500 shrink-0">–</span>

              <div className="rounded-md bg-slate-100 shrink-0 min-w-[46px] max-w-[55px] w-[55px] px-1.5 py-1">
                <InputControl
                  name={`${measureKey}-${sizeId}-max`}
                  className="min-w-0"
                  inputClassName={matrixInputClass}
                  placeholder="max"
                  value={cellData?.max ?? ""}
                  maxLength={6}
                  onChange={(e) =>
                    updateCell(sizeId, measureKey, "max", e.target.value)
                  }
                />
              </div>
              <span className="text-xs text-slate-600 shrink-0">
                {currentMeasurement}
              </span>
            </div>
          );
        },
        header: () =>
          isPreview ? (
            <span>{sizesRef.current.find((s) => s.id === sizeId)?.name ?? ""}</span>
          ) : (
            <div className="flex items-center gap-1.5 pr-[26px]">
              <InputControl
                name={`${sizeId}-name`}
                className="min-w-0 max-w-[76px] w-[64px] shrink-0 text-center"
                inputClassName="bg-white !min-h-[40px] !px-2 !py-1 !text-sm text-center"
                value={sizesRef.current.find((s) => s.id === sizeId)?.name ?? ""}
                maxLength={12}
                onChange={(e) => updateSizeName(sizeId, e.target.value)}
              />
              <button
                type="button"
                className="shrink-0"
                title="Delete size column"
                onClick={() => deleteSizeColumn(sizeId)}
              >
                <TrashIcon className="w-5 h-5 hover:text-red-500" />
              </button>
            </div>
          ),
      });
    });
    return [...base, ...dynamic];
  }

  const columns = useMemo(
    () => buildColumns(),
    // Recompute when size columns are added/removed (signature), not when labels/cells edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sizeColumnSignature, isPreview, clothingType, currentMeasurement, optionalChoiceSignature]
  );

  const DropdownIndicator = (props) => (
    <components.DropdownIndicator {...props}>
      <ChevronDownIcon className="w-4 h-4 ml-2 text-[var(--clr-text)]" />
    </components.DropdownIndicator>
  );

  const brandOptions = brands.map((b) => ({ value: b.id, label: b.name }));
  const genderOptions = [
    { value: SIZING_GENDER.MALE, label: "Male" },
    { value: SIZING_GENDER.FEMALE, label: "Female" },
  ];
  const clothingOptions = [
    { value: SIZING_CLOTHING.TOP, label: "Top" },
    { value: SIZING_CLOTHING.BOTTOM, label: "Bottom" },
    { value: SIZING_CLOTHING.FULL_BODY, label: "Full body" },
    ...(user?.isLingerieChartEnabled
      ? [{ value: SIZING_CLOTHING.LINGERIE, label: "Lingerie" }]
      : []),
  ];

  const validateBeforeSave = () => {
    if (!brandId) return toast.error("Select a brand."), false;
    if (!sanitizePlainText(displayName, 120)) return toast.error("Enter a chart name."), false;
    if (!normalizeGender(gender)) return toast.error("Select a gender."), false;
    if (!normalizeClothingType(clothingType)) return toast.error("Select a clothing type."), false;
    if (!sizes.length) return toast.error("Add at least one size column with measurements."), false;
    const required = getPrimaryMeasurementKeys(clothingType);
    for (const s of sizes) {
      for (const key of required) {
        if (!s[key] || !isValidMeasurementValue(s[key].min) || !isValidMeasurementValue(s[key].max)) {
          toast.error("Missing required measurement values.");
          return false;
        }
        const order = validateMinMaxOrder(s[key]);
        if (!order.ok) {
          toast.error(order.message);
          return false;
        }
      }
      for (const key of MEASUREMENT_KEYS) {
        if (s[key] != null) {
          if (isEmptyMeasurementCell(s[key])) continue;
          if (!isValidMeasurementValue(s[key].min) || !isValidMeasurementValue(s[key].max)) {
            toast.error("Use decimal values up to 5 digits total and max 2 decimal places (e.g. 100.50).");
            return false;
          }
          const order = validateMinMaxOrder(s[key]);
          if (!order.ok) {
            toast.error(order.message);
            return false;
          }
        }
      }
    }
    return true;
  };

  const buildPayload = () => {
    const sizesPayload = sizes.map((s) => {
      const row = { name: sanitizePlainText(s.name, 12) || "Size" };
      MEASUREMENT_KEYS.forEach((k) => {
        if (s[k] == null || isEmptyMeasurementCell(s[k])) {
          row[k] = null;
        } else {
          row[k] = {
            min: rangeOrNull(s[k].min),
            max: rangeOrNull(s[k].max),
          };
        }
      });
      return row;
    });
    const resolvedItemId =
      itemId ||
      TEMPLATE_BY_PICK[`${gender}:${clothingType}`] ||
      "";
    const itemBody = resolvedItemId
      ? { id: resolvedItemId, sizes: sizesPayload }
      : { sizes: sizesPayload };
    return {
      items: [itemBody],
      chartName: sanitizePlainText(displayName, 120),
      brandId,
      unit: currentMeasurement,
      [VENDOR_SIZING_CHART_FIELD_MAP.gender]: normalizeGender(gender),
      [VENDOR_SIZING_CHART_FIELD_MAP.clothingType]: normalizeClothingType(clothingType),
    };
  };

  const onSaveChart = async () => {
    if (!validateBeforeSave()) return;
    setSaving(true);
    setSavingError("");
    try {
      const payload = buildPayload();
      if (isCreate) {
        await createVendorSizingChart(payload);
      } else {
        await patchVendorSizingChart({ id: routeChartId, data: payload });
      }
      toast.success("Chart saved.");
      navigate("/sizing-charts", {
        state: isCreate
          ? { createdChartName: sanitizePlainText(displayName, 120) || "Sizing chart" }
          : null,
      });
    } catch (err) {
      setSavingError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Card className="min-h-[200px] flex items-center justify-center">
        <Loader />
      </Card>
    );
  }

  return (
    <>
      <ErrorMessage message={savingError} className="mb-4" />
      {error ? <ErrorMessage message={error} className="mb-4" /> : null}

      {!isPreview && (
        <Card className="flex flex-col gap-4 mb-4">
          <h2 className="page-subtitle">Chart Details</h2>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div className="min-w-0">
              <InputLabel label="Brand" id="chart-brand" isRequired />
              <Select
                inputId="chart-brand"
                classNamePrefix="react-select"
                className="custom-select"
                options={brandOptions}
                value={brandOptions.find((o) => o.value === brandId) || null}
                onChange={(opt) => setBrandId(opt?.value || "")}
                components={{ DropdownIndicator }}
              />
            </div>
            <div className="min-w-0">
              <InputControl
                label="Chart Name"
                placeholder="Enter chart name"
                name="chart-display-name"
                isRequired
                value={displayName}
                onChange={(e) => setDisplayName(sanitizePlainText(e.target.value, 120))}
              />
            </div>
            <div className="min-w-0">
              <InputLabel label="Gender" id="chart-gender" isRequired />
              <Select
                inputId="chart-gender"
                classNamePrefix="react-select"
                className="custom-select"
                options={genderOptions}
                value={genderOptions.find((o) => o.value === gender) || null}
                onChange={(opt) => {
                  const v = opt?.value;
                  if (v) applyGenderAndClothing(v, clothingType);
                }}
                components={{ DropdownIndicator }}
              />
            </div>
            <div className="min-w-0">
              <InputLabel label="Clothing Type" id="chart-clothing" isRequired />
              <Select
                inputId="chart-clothing"
                classNamePrefix="react-select"
                className="custom-select"
                options={clothingOptions}
                value={clothingOptions.find((o) => o.value === clothingType) || null}
                onChange={(opt) => {
                  const v = opt?.value;
                  if (v) applyGenderAndClothing(gender, v);
                }}
                components={{ DropdownIndicator }}
              />
            </div>
          </div>
        </Card>
      )}

      <Card className="flex flex-col gap-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-1">
            <p className="page-subtitle">
              Sizing Chart
            </p>
            <p className="text-sm text-slate-600 mt-1">
              Configure sizing chart details by adding measurement categories, sizes and values. Each clothing type has one or more primary measurement that cannot be removed. These represent the base measurements required to match a size to an avatar's measurements.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 lg:justify-end flex-shrink-0">
            <MeasurementUnitToggle value={currentMeasurement} onChange={setCurrentMeasurement} />
            {!isPreview && (
              <button type="button" className="btn-solid btn-mini" onClick={addSizeColumn}>
                <PlusIcon className="btn-prefix-icon" />
                Add Size
              </button>
            )}
          </div>
        </div>

        <div>
          {/* <h3 className="text-sm font-semibold text-slate-800 mb-3">Measurement matrix</h3> */}
          <ReactTable
            columns={columns}
            data={chartTableData}
            isLoading={false}
            totalPages={1}
            emptyMessage="No chart data found..."
            headerCellClassName="py-2.5 px-[18px] font-normal text-sm text-start"
            bodyCellClassName="py-2.5 px-[18px]"
          />
        </div>

        {!isPreview ? (
          <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-100 pt-4">
            <button
              type="button"
              className="btn-outline btn-mini min-h-[44px] px-5"
              onClick={() => navigate("/sizing-charts")}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`btn-solid btn-mini min-h-[44px] px-5 ${isSaving ? "loading" : ""}`}
              onClick={onSaveChart}
              disabled={!sizes.length || !brandId || !displayName || !clothingType}
            >
              {isCreate
              ? isSaving ? "Creating chart..." : "Create Chart"
              : isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        ) : null}
      </Card>
    </>
  );
}
